import React from "react";
import { useSkin } from "../skins/SkinContext";
import { roughEls, rg } from "../skins/rough";

/**
 * Skinnable PRIMITIVES (Anivaze v3). Each renders CLEAN under the Studio skin and
 * HAND-DRAWN (Rough.js, seeded) under the hand-drawn skin — the same call, two
 * looks. Scenes/actors compose these instead of hardcoding SVG, so swapping the
 * skin re-renders the whole video. See design-plan/v3/PLAN.md §4–5.
 *
 * Animate via a parent <g transform>, not by changing geometry props, to keep
 * the hand-drawn seed stable (no flicker).
 */

interface ShapeProps {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  seed?: number;
  opacity?: number;
}

const seedFrom = (a: number, b: number, s?: number) => s ?? (Math.abs(Math.round(a * 7 + b * 13)) % 9999) + 1;

export const SkRect: React.FC<ShapeProps & { x: number; y: number; w: number; h: number; rx?: number }> = React.memo(
  ({ x, y, w, h, rx = 0, fill = "none", stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} stroke={stroke ?? "none"} strokeWidth={strokeWidth ?? skin.strokeWidth} opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.rectangle(x, y, w, h, {
      roughness: skin.roughness, seed: seedFrom(x, y, seed),
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
      fill: fill === "none" ? undefined : fill, fillStyle: "solid",
    }))}</g>;
  },
);

export const SkEllipse: React.FC<ShapeProps & { cx: number; cy: number; rx: number; ry: number }> = React.memo(
  ({ cx, cy, rx, ry, fill = "none", stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill={fill} stroke={stroke ?? "none"} strokeWidth={strokeWidth ?? skin.strokeWidth} opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.ellipse(cx, cy, rx * 2, ry * 2, {
      roughness: skin.roughness, seed: seedFrom(cx, cy, seed),
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
      fill: fill === "none" ? undefined : fill, fillStyle: "solid",
    }))}</g>;
  },
);

export const SkCircle: React.FC<ShapeProps & { cx: number; cy: number; r: number }> = React.memo(
  ({ cx, cy, r, fill = "none", stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke ?? "none"} strokeWidth={strokeWidth ?? skin.strokeWidth} opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.circle(cx, cy, r * 2, {
      roughness: skin.roughness, seed: seedFrom(cx, cy, seed),
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
      fill: fill === "none" ? undefined : fill, fillStyle: "solid",
    }))}</g>;
  },
);

export const SkLine: React.FC<ShapeProps & { x1: number; y1: number; x2: number; y2: number }> = React.memo(
  ({ x1, y1, x2, y2, stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke ?? skin.ink} strokeWidth={strokeWidth ?? skin.strokeWidth} strokeLinecap="round" opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.line(x1, y1, x2, y2, {
      roughness: skin.roughness, seed: seedFrom(x1, y2, seed),
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
    }))}</g>;
  },
);

export const SkPolygon: React.FC<ShapeProps & { points: Array<[number, number]> }> = React.memo(
  ({ points, fill = "none", stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <polygon points={points.map((p) => p.join(",")).join(" ")} fill={fill} stroke={stroke ?? "none"} strokeWidth={strokeWidth ?? skin.strokeWidth} opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.polygon(points, {
      roughness: skin.roughness, seed: seedFrom(points[0]?.[0] ?? 0, points[0]?.[1] ?? 0, seed),
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
      fill: fill === "none" ? undefined : fill, fillStyle: "solid",
    }))}</g>;
  },
);

export const SkPath: React.FC<ShapeProps & { d: string }> = React.memo(
  ({ d, fill = "none", stroke, strokeWidth, seed, opacity = 1 }) => {
    const skin = useSkin();
    if (skin.roughness === 0) {
      return <path d={d} fill={fill} stroke={stroke ?? "none"} strokeWidth={strokeWidth ?? skin.strokeWidth} strokeLinecap="round" strokeLinejoin="round" opacity={opacity} />;
    }
    return <g opacity={opacity}>{roughEls(rg.path(d, {
      roughness: skin.roughness, seed: seed ?? 1,
      stroke: skin.inkOutlines ? skin.ink : stroke ?? skin.ink, strokeWidth: strokeWidth ?? skin.strokeWidth,
      fill: fill === "none" ? undefined : fill, fillStyle: "solid",
    }))}</g>;
  },
);

export const SkText: React.FC<{
  x: number; y: number; children: React.ReactNode; size?: number; weight?: number; anchor?: "start" | "middle" | "end"; fill?: string;
}> = ({ x, y, children, size = 16, weight = 600, anchor = "middle", fill }) => {
  const skin = useSkin();
  return (
    <text x={x} y={y} textAnchor={anchor} fontSize={size} fontWeight={weight} fill={fill ?? skin.textInk} fontFamily={skin.fontFamily}>
      {children}
    </text>
  );
};
